/**
 * Client-only registration: Supabase Auth + public.users + GoHighLevel Create Contact.
 *
 * API reference: https://marketplace.gohighlevel.com/docs/ghl/contacts/create-contact
 * Country codes: https://marketplace.gohighlevel.com/docs/other/country
 */
import { parsePhoneNumberFromString, getCountries, getCountryCallingCode } from "libphonenumber-js";
import type { CountryCode } from "libphonenumber-js";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";
import { PENDING_SIGNUP_COUPON_KEY } from "@/lib/couponConstants";

// SECURITY: the GoHighLevel token used to live here and shipped in the client bundle.
// It now lives server-side only, in the `ghl-sync` Supabase edge function. This client
// never sees a GHL credential. (The ongoing GHL feature surface is the GoHighLevel plugin.)

export type RegistrationFormInput = {
  email: string;
  password: string;
  username: string;
  firstName: string;
  lastName: string;
  countryIso: CountryCode;
  phoneNational: string;
  couponCode?: string;
};

export type RegistrationSuccess = {
  ok: true;
  email: string;
  /** True when Supabase sends a confirmation email and no session is returned */
  emailVerificationRequired: boolean;
  ghlSynced: boolean;
  ghlWarning?: string;
};

export type RegistrationFailure = {
  ok: false;
  error: string;
};

export type RegistrationResult = RegistrationSuccess | RegistrationFailure;

/** At least 2 characters; letters (any script), spaces, apostrophes, hyphens, periods */
const NAME_RE = /^[\p{L}][\p{L}\s'.-]{1,59}$/u;
const USERNAME_RE = /^[a-z][a-z0-9_]{2,23}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

let countryOptionsCache: { iso: CountryCode; dial: string; label: string }[] | null = null;

export function getPhoneCountryOptions(): { iso: CountryCode; dial: string; label: string }[] {
  if (countryOptionsCache) return countryOptionsCache;
  const list = getCountries()
    .map((iso) => {
      try {
        const dial = `+${getCountryCallingCode(iso)}`;
        return { iso: iso as CountryCode, dial, label: `${iso} (${dial})` };
      } catch {
        return null;
      }
    })
    .filter((x): x is NonNullable<typeof x> => x !== null)
    .sort((a, b) => a.iso.localeCompare(b.iso));
  countryOptionsCache = list;
  return list;
}

export function isStrongPassword(password: string): boolean {
  if (password.length < 8 || password.length > 128) return false;
  if (!/[A-Z]/.test(password)) return false;
  if (!/[a-z]/.test(password)) return false;
  if (!/[0-9]/.test(password)) return false;
  if (!/[^A-Za-z0-9]/.test(password)) return false;
  return true;
}

export function validatePersonName(name: string, label: string): string | null {
  const t = name.trim();
  if (t.length < 2) return `${label} must be at least 2 characters`;
  if (t.length > 60) return `${label} is too long`;
  if (!NAME_RE.test(t))
    return `${label} may only include letters, spaces, apostrophes, periods, and hyphens`;
  return null;
}

export function validateUsername(username: string): string | null {
  const n = username.trim().toLowerCase();
  if (!USERNAME_RE.test(n)) {
    return "Username must be 3–24 characters, start with a letter, and use only lowercase letters, numbers, and underscores";
  }
  return null;
}

export function validateEmail(email: string): string | null {
  const t = email.trim();
  if (!EMAIL_RE.test(t)) return "Enter a valid email address";
  return null;
}

export function parseAndValidatePhone(
  countryIso: CountryCode,
  phoneNational: string
): { e164: string; dial: string } | { error: string } {
  const digits = phoneNational.replace(/[^\d]/g, "");
  if (digits.length < 6) return { error: "Enter a valid phone number" };
  const parsed = parsePhoneNumberFromString(digits, countryIso);
  if (!parsed || !parsed.isValid()) {
    return { error: "Phone number is not valid for the selected country" };
  }
  const dial = `+${parsed.countryCallingCode}`;
  return { e164: parsed.format("E.164"), dial };
}

async function isUsernameTaken(normalizedUsername: string): Promise<boolean> {
  if (!isSupabaseConfigured) return false;
  const { data, error } = await supabase
    .from("users")
    .select("id")
    .eq("username", normalizedUsername)
    .maybeSingle();
  if (error && error.code !== "PGRST116") {
    console.warn("username check:", error);
    throw new Error("Could not verify username availability. Try again.");
  }
  return !!data;
}

async function insertPublicUserRow(params: {
  authUserId: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  mobileE164: string;
  countryIso: string;
  dial: string;
}): Promise<void> {
  const now = new Date().toISOString();
  const row = {
    username: params.username,
    email: params.email,
    provider: "email",
    providerId: params.authUserId,
    first_name: params.firstName,
    last_name: params.lastName,
    mobile_number: params.mobileE164,
    country: params.countryIso,
    dial: params.dial,
    createdAt: now,
    userKey: params.authUserId,
    updated_at: now,
    is_online: false,
  };

  const { error } = await supabase.from("users").insert(row);
  if (error) {
    console.error("public.users insert:", error);
    throw new Error(
      error.message?.includes("duplicate") || error.code === "23505"
        ? "That username or email is already registered"
        : "Account was created but saving your profile failed. Contact support with your email."
    );
  }
}

/**
 * Create Contact — POST /contacts/
 * @see https://marketplace.gohighlevel.com/docs/ghl/contacts/create-contact
 */
async function createGoHighLevelContact(payload: {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  country: string;
  dial: string;
  username: string;
  authUserId: string;
  couponCode?: string;
}): Promise<{ ok: true } | { ok: false; message: string }> {
  // Delegate to the server-side ghl-sync edge function (token held server-side).
  // Fail-soft: any error is returned as { ok: false } so signup is never blocked.
  try {
    const { data, error } = await supabase.functions.invoke("ghl-sync", {
      body: {
        email: payload.email,
        firstName: payload.firstName,
        lastName: payload.lastName,
        phone: payload.phone,
        country: payload.country,
        dial: payload.dial,
        username: payload.username,
        authUserId: payload.authUserId,
        couponCode: payload.couponCode ?? "",
      },
    });
    if (error) return { ok: false, message: error.message || "CRM sync failed" };
    if (data && typeof data === "object" && "ok" in data) {
      return (data as { ok: boolean; message?: string }).ok
        ? { ok: true }
        : { ok: false, message: (data as { message?: string }).message || "CRM sync failed" };
    }
    return { ok: false, message: "Unexpected CRM response" };
  } catch (e) {
    return { ok: false, message: e instanceof Error ? e.message : "Network error" };
  }
}

function validateAll(input: RegistrationFormInput): string | null {
  const e = validateEmail(input.email);
  if (e) return e;
  const u = validateUsername(input.username);
  if (u) return u;
  const fn = validatePersonName(input.firstName, "First name");
  if (fn) return fn;
  const ln = validatePersonName(input.lastName, "Last name");
  if (ln) return ln;
  if (!isStrongPassword(input.password)) {
    return "Password must be at least 8 characters and include uppercase, lowercase, a number, and a special character";
  }
  const phone = parseAndValidatePhone(input.countryIso, input.phoneNational);
  if ("error" in phone) return phone.error;
  return null;
}

/**
 * Full registration: validate → username availability → Auth signUp → public.users → GHL contact.
 */
export async function registerAccount(input: RegistrationFormInput): Promise<RegistrationResult> {
  if (!isSupabaseConfigured) {
    return { ok: false, error: "Authentication service is not configured" };
  }

  const normalizedUsername = input.username.trim().toLowerCase();
  const err = validateAll({ ...input, username: normalizedUsername });
  if (err) return { ok: false, error: err };

  const phone = parseAndValidatePhone(input.countryIso, input.phoneNational);
  if ("error" in phone) return { ok: false, error: phone.error };

  try {
    const taken = await isUsernameTaken(normalizedUsername);
    if (taken) return { ok: false, error: "That username is already taken" };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : "Username check failed" };
  }

  if (input.couponCode?.trim()) {
    localStorage.setItem(PENDING_SIGNUP_COUPON_KEY, input.couponCode.trim());
  } else {
    localStorage.removeItem(PENDING_SIGNUP_COUPON_KEY);
  }

  const redirectUrl = `${window.location.origin}`;

  const { data, error } = await supabase.auth.signUp({
    email: input.email.trim(),
    password: input.password,
    options: {
      emailRedirectTo: redirectUrl,
      data: {
        username: normalizedUsername,
        first_name: input.firstName.trim(),
        last_name: input.lastName.trim(),
      },
    },
  });

  if (error) {
    let msg = error.message || "Signup failed";
    if (msg.includes("User already registered")) {
      msg = "An account with this email already exists. Please sign in instead.";
    } else if (msg.includes("Password")) {
      msg = "Password does not meet the provider requirements";
    }
    return { ok: false, error: msg };
  }

  const user = data.user;
  if (!user?.id) {
    return { ok: false, error: "Signup did not return a user. Please try again." };
  }

  try {
    await insertPublicUserRow({
      authUserId: user.id,
      username: normalizedUsername,
      email: input.email.trim(),
      firstName: input.firstName.trim(),
      lastName: input.lastName.trim(),
      mobileE164: phone.e164,
      countryIso: input.countryIso,
      dial: phone.dial,
    });
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : "Saving profile failed" };
  }

  let ghlSynced = false;
  let ghlWarning: string | undefined;
  const ghl = await createGoHighLevelContact({
    email: input.email.trim(),
    firstName: input.firstName.trim(),
    lastName: input.lastName.trim(),
    phone: phone.e164,
    country: input.countryIso,
    dial: phone.dial,
    username: normalizedUsername,
    authUserId: user.id,
    couponCode: input.couponCode,
  });
  if (ghl.ok) {
    ghlSynced = true;
  } else {
    ghlWarning = `CRM sync skipped: ${ghl.message}`;
  }

  const emailVerificationRequired = !data.session;

  return {
    ok: true,
    email: input.email.trim(),
    emailVerificationRequired,
    ghlSynced,
    ghlWarning,
  };
}
